import { useCallback, useEffect, useRef, useState } from 'react';
import { updateServiceRequestStatus } from '../api/updateServiceRequestStatusApiService';
import {
  TECHNICIAN_UPDATE_SR_STATUS_LABELS as L,
  type TechnicianMissedVisitReasonId,
} from '../constants/labels';
import { UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES } from '../errors';
import { getTechnicianAllowedNextStatuses } from '../pages/viewServiceRequest/viewServiceRequestHelpers';
import type { ServiceRequestStatus } from '../types/serviceRequestListType';

interface UseUpdateTaskStatusOptions {
  serviceRequestId: string | undefined;
  currentStatus: ServiceRequestStatus | undefined;
  /** Receives the status that was just submitted so the caller can surface a success message and refetch. */
  onSuccess?: (submittedStatus: ServiceRequestStatus) => void;
  /**
   * Called when the backend rejects the submit with 403 — i.e. the technician's
   * `task_update_status` privilege was revoked while they had the screen open.
   * The modal is closed before this fires so the caller can show a snackbar.
   */
  onPermissionDenied?: () => void;
}

/**
 * Two-step technician flow:
 * - `status` — pick the new status (RR-624 single-step flow for every target).
 * - `details` — the "Unfulfilled Visit" step (RR-622), reached only when the
 *   selected target is `unfulfilled` and the technician clicks "Zmiana statusu".
 */
export type UpdateTaskStatusStep = 'status' | 'details';

export interface UseUpdateTaskStatusReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;

  /** Which step of the modal is currently shown. */
  step: UpdateTaskStatusStep;
  /**
   * Step-1 primary action for the `unfulfilled` target: validates that a status
   * is selected, then advances to the "Unfulfilled Visit" details step.
   */
  goToDetailsStep: () => void;
  /** Returns from the details step back to the status-selection step. */
  goBackToStatusStep: () => void;

  newStatus: ServiceRequestStatus | '';
  setNewStatus: (status: ServiceRequestStatus) => void;
  newStatusError: string;

  notes: string;
  setNotes: (value: string) => void;
  notesError: string;

  /**
   * Missed-visit reason (RR-622) — only relevant when `newStatus` is
   * `unfulfilled`. Mandatory before submit; drives the reason dropdown.
   */
  missedVisitReason: TechnicianMissedVisitReasonId | '';
  setMissedVisitReason: (id: TechnicianMissedVisitReasonId) => void;
  missedVisitReasonError: string;

  /** Technician-allowed next statuses for the current status (drives the dropdown). */
  allowedStatuses: ServiceRequestStatus[];

  isSubmitting: boolean;
  submitError: string;
  clearSubmitError: () => void;
  /** Stays true except while a submit is in flight, so the user can click and surface required-field errors. */
  canSubmit: boolean;
  submit: () => Promise<void>;
}

/**
 * Owns the technician "Update Task Status" flow (RR-624) — the single-step
 * counterpart to {@link useUpdateServiceRequestStatus} (admin). Reuses the
 * shared `PATCH /service-requests/{id}/status` API service, but applies the
 * narrower technician transition map and sends no `notify` flag (the backend
 * always notifies on the technician path).
 */
export const useUpdateTaskStatus = ({
  serviceRequestId,
  currentStatus,
  onSuccess,
  onPermissionDenied,
}: UseUpdateTaskStatusOptions): UseUpdateTaskStatusReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<UpdateTaskStatusStep>('status');
  const [newStatus, setNewStatusState] = useState<ServiceRequestStatus | ''>(
    ''
  );
  const [notes, setNotesState] = useState('');
  const [newStatusError, setNewStatusError] = useState('');
  const [notesError, setNotesError] = useState('');
  const [missedVisitReason, setMissedVisitReasonState] = useState<
    TechnicianMissedVisitReasonId | ''
  >('');
  const [missedVisitReasonError, setMissedVisitReasonError] = useState('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const [submitError, setSubmitError] = useState('');

  const allowedStatuses = currentStatus
    ? getTechnicianAllowedNextStatuses(currentStatus)
    : [];

  const resetForm = useCallback(() => {
    setStep('status');
    setNewStatusState('');
    setNotesState('');
    setNewStatusError('');
    setNotesError('');
    setMissedVisitReasonState('');
    setMissedVisitReasonError('');
    setSubmitError('');
  }, []);

  const open = useCallback(() => {
    resetForm();
    setIsOpen(true);
  }, [resetForm]);

  const close = useCallback(() => {
    if (isSubmitting) {
      return;
    }
    setIsOpen(false);
    resetForm();
  }, [isSubmitting, resetForm]);

  // Reset when the underlying request/status changes while the modal is open.
  useEffect(() => {
    if (!isOpen) {
      return;
    }
    resetForm();
  }, [serviceRequestId, currentStatus, isOpen, resetForm]);

  const goToDetailsStep = useCallback(() => {
    if (!newStatus) {
      setNewStatusError(L.ERROR_REQUIRED);
      return;
    }
    setStep('details');
  }, [newStatus]);

  const goBackToStatusStep = useCallback(() => {
    setStep('status');
  }, []);

  const setNewStatus = useCallback((status: ServiceRequestStatus) => {
    setNewStatusState(status);
    setNewStatusError('');
    // The missed-visit reason only applies to `unfulfilled`; clear it (and any
    // surfaced error) when switching to a different target so a stale reason
    // can't be carried into the payload.
    if (status !== 'unfulfilled') {
      setMissedVisitReasonState('');
      setMissedVisitReasonError('');
    }
  }, []);

  const setNotes = useCallback((value: string) => {
    setNotesState(value);
    if (value.trim().length <= L.NOTES_MAX_LENGTH) {
      setNotesError('');
    }
  }, []);

  const setMissedVisitReason = useCallback(
    (id: TechnicianMissedVisitReasonId) => {
      setMissedVisitReasonState(id);
      setMissedVisitReasonError('');
      // Picking a preset reason clears any "Other" free-text error; the comment
      // value itself is left intact so a half-typed note isn't lost on toggle.
      if (id !== 'other') {
        setNotesError('');
      }
    },
    []
  );

  const clearSubmitError = useCallback(() => setSubmitError(''), []);

  const canSubmit = !isSubmitting;

  const submit = useCallback(async () => {
    if (!serviceRequestId || isSubmittingRef.current) {
      return;
    }

    const isUnfulfilled = newStatus === 'unfulfilled';

    let hasError = false;
    if (!newStatus) {
      setNewStatusError(L.ERROR_REQUIRED);
      hasError = true;
    }
    // Missed-visit flow (RR-622): the reason is mandatory and the comment is
    // required only when the reason is "Other".
    if (isUnfulfilled && !missedVisitReason) {
      setMissedVisitReasonError(L.ERROR_REQUIRED);
      hasError = true;
    }
    if (
      isUnfulfilled &&
      missedVisitReason === 'other' &&
      notes.trim().length === 0
    ) {
      setNotesError(L.ERROR_REQUIRED);
      hasError = true;
    }
    if (notes.trim().length > L.NOTES_MAX_LENGTH) {
      setNotesError(
        isUnfulfilled ? L.ERROR_COMMENT_TOO_LONG : L.ERROR_NOTES_TOO_LONG
      );
      hasError = true;
    }
    if (hasError) {
      return;
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // Missed-visit target (RR-622): send the structured `missed_visit_reason`
      // code alongside the raw comment, and ask the BE to notify the client +
      // super admins. The BE stamps the technician's identity onto the stored
      // note. Other targets keep the plain note-only payload.
      const result = await updateServiceRequestStatus({
        serviceRequestId,
        newStatus: newStatus as ServiceRequestStatus,
        adminNotes: notes,
        ...(isUnfulfilled && missedVisitReason
          ? { missedVisitReason, notify: true }
          : {}),
      });
      if (result.success) {
        const submittedStatus = newStatus as ServiceRequestStatus;
        setIsOpen(false);
        resetForm();
        onSuccess?.(submittedStatus);
      } else if (result.permissionDenied) {
        // Privilege revoked mid-session: close the modal and let the caller
        // surface the permission-denied snackbar.
        setIsOpen(false);
        resetForm();
        onPermissionDenied?.();
      } else {
        setSubmitError(
          result.error ??
            UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES.DEFAULT_ERROR
        );
      }
    } catch {
      setSubmitError(
        UPDATE_SERVICE_REQUEST_STATUS_ERROR_MESSAGES.UNEXPECTED_ERROR
      );
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [
    serviceRequestId,
    newStatus,
    notes,
    missedVisitReason,
    onSuccess,
    onPermissionDenied,
    resetForm,
  ]);

  return {
    isOpen,
    open,
    close,
    step,
    goToDetailsStep,
    goBackToStatusStep,
    newStatus,
    setNewStatus,
    newStatusError,
    notes,
    setNotes,
    notesError,
    missedVisitReason,
    setMissedVisitReason,
    missedVisitReasonError,
    allowedStatuses,
    isSubmitting,
    submitError,
    clearSubmitError,
    canSubmit,
    submit,
  };
};
